export interface RejectionSummary {
  /** short, human label for the tag */
  label: string;
  /** full text for the tooltip, including the raw payload */
  detail: string;
  /** true when we recognised a structured Meta error rather than free text */
  isMetaError: boolean;
}

// Meta error subcodes we have actually seen come back from the Graph API.
// Anything unmapped falls back to a keyword sniff and then a generic label,
// so a new subcode degrades to "Meta error" instead of dumping raw JSON.
const SUBCODE_LABELS: Record<string, string> = {
  '1815199': 'Instagram access error',
  '2875030': 'Reel not boostable',
  '3858750': 'Ad set ended',
};

/**
 * Turn a stored rejection_reason into something displayable.
 *
 * Meta rejections are saved as the raw Graph API error JSON, which is far too
 * long for a table cell. Free-text reasons (e.g. a manual "other reason") are
 * passed through untouched.
 */
export function summarizeRejectionReason(raw: string | null | undefined): RejectionSummary {
  const text = (raw ?? '').trim();
  if (!text) return { label: '', detail: '', isMetaError: false };

  let error: any = null;
  try {
    const parsed = JSON.parse(text);
    error = parsed?.error ?? parsed;
  } catch {
    // Not JSON — a plain, human-written reason.
  }

  if (!error || typeof error !== 'object') {
    return { label: text, detail: text, isMetaError: false };
  }

  const subcode = error.error_subcode != null ? String(error.error_subcode) : '';
  const title = typeof error.error_user_title === 'string' ? error.error_user_title : '';
  const message = error.error_user_msg || error.message || '';

  let label = SUBCODE_LABELS[subcode];
  if (!label) {
    const haystack = `${title} ${message}`.toLowerCase();
    label = /music|audio|licen[cs]/.test(haystack) ? 'Music licence error' : 'Meta error';
  }

  const readable = [title, message].filter(Boolean).join(' — ');
  return {
    label,
    detail: readable ? `${readable}\n\n${text}` : text,
    isMetaError: true,
  };
}
